import { memo, useEffect } from 'react'

import { useHandler } from '@surglogs-app/hooks'
import { useAuthTokenRevokePostMutation } from '@surglogs-app/network'
import {
  useLocationQuery,
  redirectToExternalUrl,
  getStandaloneLoginRoute,
  getUnauthorizedRoute,
} from '@surglogs-app/routing'

import { isExternalUserToken } from '../helpers'
import { useAuthToken } from '../hooks'

import { TIMER_REFRESH_TIMESTAMP } from './PrivateRouteSession'

interface LogoutProps {
  referrer?: string
  organization?: string
}

export const Logout = memo(({ referrer, organization }: LogoutProps) => {
  const { reason } = useLocationQuery<{
    reason?: 'inactivity' | 'password_changed' | 'external_user_unauthorized'
  }>()
  const { token, refreshToken, clearAuthData } = useAuthToken()

  const { mutateAsync: revokeJwtTokenMutateAsync } =
    useAuthTokenRevokePostMutation({ opts: { throwOnError: false } })

  const logoutHandler = useHandler(
    async (
      logoutMutateAsync: () => Promise<void>,
      redirectUrl: string,
      isNewAuth = false,
    ) => {
      try {
        await logoutMutateAsync()
      } catch (error) {
        // we don't want to throw 401 -> log into sentry,
        // because that means token already expired
        if ((error as any)?.response?.status === 401) {
          return
        }
        throw error
      } finally {
        clearAuthData()
        sessionStorage.removeItem(TIMER_REFRESH_TIMESTAMP)

        if (isExternalUserToken(token)) {
          redirectToExternalUrl(getUnauthorizedRoute({ external: true }))

          return
        }

        if (isNewAuth) {
          redirectToExternalUrl(redirectUrl)
        } else {
          window.location.pathname = redirectUrl
        }
      }
    },
  )

  useEffect(() => {
    if (token) {
      logoutHandler(
        () =>
          revokeJwtTokenMutateAsync({
            access_token: token,
            refresh_token: refreshToken,
          }),
        getStandaloneLoginRoute({ referrer, reason, organization }),
        true,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, reason, logoutHandler])

  return null
})
